/**
 * Shared helpers for VSC report row/card.
 */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toLocalDateString } from '@/utils/format-date-utils';
import type {
  VscReport,
  VscReportFilters,
  ExportVscReportsParams,
  GetAllVscReportsParams,
} from '@/types/vsc-report.types';
import {
  DEFAULT_VSC_REPORT_FILTERS,
  VSC_REPORT_STATUS_CONFIG,
  type VscReportPageActions,
  type VscReportStatusConfig,
} from './vsc-report-configs';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRowPermission } from '@/hooks/usePageActions';

/** Precomputed row/card display fields. */
export interface DerivedVscReportDisplay {
  statusConfig: VscReportStatusConfig;
  StatusIcon: VscReportStatusConfig['icon'];
  volunteerName: string;
  sessionDisplayText: string;
  establishmentName: string | undefined;
  entryDateText: string;
}

/** Edit / validate disabled flags driven by `validated` vs `submitted`. */
export interface VscReportActionState {
  editDisabled: boolean;
  editTooltip: string;
  validateDisabled: boolean;
  validateTooltip: string;
}

/** Precomputed detail-panel display fields. */
export interface DerivedVscDetailDisplay {
  statusConfig: VscReportStatusConfig;
  StatusIcon: VscReportStatusConfig['icon'];
  volunteerName: string;
  headerSubtitle: string;
  establishmentName: string;
  reportDateLabel: string;
  sessionTimeRange: string | undefined;
  statusColor: 'success' | 'error';
}

/** Inputs for {@link getVscReportDetailCapabilities}. */
export interface VscReportDetailCapabilitiesInput {
  vscReport: VscReport;
  presentationMode: 'view' | 'edit';
  userId?: string | null;
  isVolunteer: boolean;
  isVsc: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isCoordinator: boolean;
  canValidate: boolean;
  draftRating: number | null;
}

/** Whether the detail form can edit / submit / validate. */
export interface VscReportDetailCapabilities {
  editable: boolean;
  formDisabled: boolean;
  canSubmit: boolean;
  canValidateUi: boolean;
}

/** Whether `visibleAt` is in the past. */
function isVscReportVisibleNow(report: VscReport): boolean {
  return Date.now() >= new Date(report.visibleAt).getTime();
}

/** Format a VSC datetime for FR display. */
export function formatVscDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** YYYY-MM-DD at noon local (avoids UTC day-shift) or a full ISO string. */
function parseYmdOrIso(raw: string): Date {
  return new Date(raw.includes('T') ? raw : `${raw}T12:00:00`);
}

/** Long French weekday + date for detail headers. */
function formatYmd(ymd: string): string {
  return format(parseYmdOrIso(ymd), 'EEEE d MMMM yyyy', { locale: fr });
}

/** `displayName` → first+last → email → `fallback`. */
function resolveVscDisplayName(
  user:
    | {
        displayName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
  fallback = 'VSC inconnu',
): string {
  if (!user) return fallback;
  if (user.displayName?.trim()) return user.displayName.trim();
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (user.email) return user.email;
  return fallback;
}

/** Volunteer name from the nested `vscUser`. */
function deriveVscVolunteerName(vscReport: VscReport): string {
  return resolveVscDisplayName(vscReport.vscUser);
}

/** Establishment name, or `undefined` so the row can hide the subtitle. */
function deriveVscEstablishmentName(vscReport: VscReport): string | undefined {
  return vscReport.establishment?.name || undefined;
}

/** Unlock-session date, else the report date. */
function deriveVscSessionDateLabel(vscReport: VscReport): string {
  const raw = vscReport.unlockSession?.sessionDate ?? vscReport.reportDate;
  if (!raw) return 'Date inconnue';
  return format(parseYmdOrIso(typeof raw === 'string' ? raw : String(raw)), 'd MMM yyyy', {
    locale: fr,
  });
}

/** `submittedAt` short date, or em dash while still `to_fill`. */
function deriveVscEntryDateLabel(vscReport: VscReport): string {
  if (!vscReport.submittedAt) return '—';
  return format(new Date(vscReport.submittedAt), 'd MMM yyyy', { locale: fr });
}

/** Status badge + volunteer / session / establishment / entry labels for a row. */
export function deriveVscReportDisplay(vscReport: VscReport): DerivedVscReportDisplay {
  const statusConfig =
    VSC_REPORT_STATUS_CONFIG[vscReport.status] ?? VSC_REPORT_STATUS_CONFIG.to_fill;
  return {
    statusConfig,
    StatusIcon: statusConfig.icon,
    volunteerName: deriveVscVolunteerName(vscReport),
    sessionDisplayText: deriveVscSessionDateLabel(vscReport),
    establishmentName: deriveVscEstablishmentName(vscReport),
    entryDateText: deriveVscEntryDateLabel(vscReport),
  };
}

/** `validated` locks edit; validate is only enabled on `submitted`. */
export function getVscReportActionState(vscReport: VscReport): VscReportActionState {
  const editDisabled = vscReport.status === 'validated';
  const validateDisabled = vscReport.status !== 'submitted';
  return {
    editDisabled,
    editTooltip: editDisabled ? getErrorMessage('validatedVscLocked') : 'Modifier le rapport',
    validateDisabled,
    validateTooltip: validateDisabled
      ? getErrorMessage('vscMustBeSubmittedToValidate')
      : 'Valider le rapport',
  };
}

/** Local YYYY-MM-DD for the date-range picker (not UTC). */
export function formatVscFilterDateToString(d?: Date): string | undefined {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parse a picker YYYY-MM-DD to local midnight or 23:59:59. */
export function parseVscFilterDateInput(val: string, endOfDay: boolean): Date {
  const [year, month, day] = val.split('-').map(Number);
  return endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59)
    : new Date(year, month - 1, day, 0, 0, 0);
}

/**
 * Map list filters to API query params.
 * Custom range wins; week is Monday–Sunday; empty statuses/search are omitted.
 */
export function toVscReportApiFilterParams(filters: VscReportFilters): ExportVscReportsParams {
  let reportDateFrom: string | undefined;
  let reportDateTo: string | undefined;

  if (filters.customDateFrom || filters.customDateTo) {
    if (filters.customDateFrom) reportDateFrom = toLocalDateString(filters.customDateFrom);
    if (filters.customDateTo) reportDateTo = toLocalDateString(filters.customDateTo);
  } else if (filters.dateRange !== 'all') {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filters.dateRange) {
      case 'today':
        reportDateFrom = reportDateTo = toLocalDateString(startOfToday);
        break;
      case 'week': {
        const from = new Date(startOfToday);
        const daysFromMonday = (from.getDay() + 6) % 7;
        from.setDate(from.getDate() - daysFromMonday);
        const to = new Date(from);
        to.setDate(to.getDate() + 6);
        to.setHours(23, 59, 59, 999);
        reportDateFrom = toLocalDateString(from);
        reportDateTo = toLocalDateString(to);
        break;
      }
      case 'month':
        reportDateFrom = toLocalDateString(new Date(now.getFullYear(), now.getMonth(), 1));
        reportDateTo = toLocalDateString(new Date(now.getFullYear(), now.getMonth() + 1, 0));
        break;
      case 'year':
        reportDateFrom = toLocalDateString(new Date(now.getFullYear(), 0, 1));
        reportDateTo = toLocalDateString(new Date(now.getFullYear(), 11, 31));
        break;
      default:
        break;
    }
  }

  const statuses =
    filters.statuses.length > 0
      ? (filters.statuses as ('to_fill' | 'submitted' | 'validated')[])
      : undefined;

  const search = filters.search?.trim() || undefined;

  return {
    establishmentId: filters.establishmentId,
    reportDateFrom,
    reportDateTo,
    ...(statuses?.length ? { statuses } : {}),
    ...(search ? { search } : {}),
  };
}

/** List query: filter params plus page / pageSize. */
export function toVscReportListParams(
  filters: VscReportFilters,
  currentPage: number,
  itemsPerPage: number,
): GetAllVscReportsParams {
  return {
    ...toVscReportApiFilterParams(filters),
    page: currentPage,
    pageSize: itemsPerPage,
  };
}

/** Resolves edit/validate RBAC for a VSC report row. */
export function resolveVscReportPermissions(pageActions?: VscReportPageActions): {
  canEdit: boolean;
  canValidate: boolean;
} {
  return {
    canEdit: pageActions?.canEditRow ?? false,
    canValidate: pageActions?.canValidate ?? false,
  };
}

/** Overlay caller flags onto hook RBAC (`??` so `false` from the caller wins). */
export function mergeVscReportPageActions(
  pageActions: VscReportPageActions | undefined,
  rbac: VscReportPageActions,
): VscReportPageActions {
  return {
    canEditRow: pageActions?.canEditRow ?? rbac.canEditRow,
    canDeleteRow: pageActions?.canDeleteRow ?? rbac.canDeleteRow,
    canViewRow: pageActions?.canViewRow ?? rbac.canViewRow,
    canValidate: pageActions?.canValidate ?? rbac.canValidate,
  };
}

/** Prefer an injected list (dashboard section); else the paginated API page. */
export function resolveVscTableRows(
  externalVscReports: VscReport[] | undefined,
  apiData: VscReport[] | undefined,
): VscReport[] {
  return externalVscReports ?? apiData ?? [];
}

/** Deep-compare against defaults to drive the filter badge. */
export function hasVscReportActiveFilters(
  filters: VscReportFilters,
  defaultFilters: VscReportFilters = DEFAULT_VSC_REPORT_FILTERS,
): boolean {
  return JSON.stringify(filters) !== JSON.stringify(defaultFilters);
}

/** Extra column when the bulk-select checkbox is visible. */
export function getVscExpandedColSpan(showBulkSelection: boolean): number {
  return showBulkSelection ? 6 : 5;
}

/** French plural suffix for report-count labels. */
function vscCountPluralSuffix(count: number): string {
  return count > 1 ? 's' : '';
}

/** Count label for bulk-action copy (plural suffix when count > 1). */
export function formatVscReportCountLabel(count: number): string {
  return `${count} rapport${vscCountPluralSuffix(count)} VSC`;
}

/** Toolbar validate tooltip — empty selection vs count. */
export function getVscBulkValidateTooltip(count: number): string {
  if (count === 0) return getErrorMessage('selectSubmittedVscToValidate');
  return `Valider ${formatVscReportCountLabel(count)}`;
}

/** Toolbar delete tooltip — empty selection vs count. */
export function getVscBulkDeleteTooltip(count: number): string {
  if (count === 0) return getErrorMessage('selectVscToDelete');
  return `Supprimer ${formatVscReportCountLabel(count)}`;
}

/** Bulk-delete modal body (irreversible). */
export function getVscBulkDeleteDescription(count: number): string {
  return `Êtes-vous sûr de vouloir supprimer ${formatVscReportCountLabel(count)} ? Cette action est irréversible.`;
}

/** Bulk-validate modal body. */
export function getVscBulkValidateDescription(count: number): string {
  return `Êtes-vous sûr de vouloir valider ${formatVscReportCountLabel(count)} ?`;
}

/** Success toast after bulk delete. */
export function getVscBulkDeleteSuccessMessage(count: number): string {
  return `${count} rapport${vscCountPluralSuffix(count)} VSC supprimé${vscCountPluralSuffix(count)} avec succès`;
}

/** Success toast after bulk validate. */
export function getVscBulkValidateSuccessMessage(count: number): string {
  return `${count} rapport${vscCountPluralSuffix(count)} VSC validé${vscCountPluralSuffix(count)} avec succès`;
}

/** Export button tooltip — empty-filter warning unless an export is already running. */
export function getVscExportTooltip(isEmptyForExport: boolean, isExporting: boolean): string {
  if (isEmptyForExport && !isExporting) {
    return getErrorMessage('exportEmpty');
  }
  return 'Exporter la liste des rapports VSC en Excel';
}

/** Single-delete modal item name (establishment, or a generic fallback). */
export function formatVscDeleteModalItemName(vscReport: VscReport | null | undefined): string {
  if (!vscReport) return 'ce rapport VSC';
  return `le rapport VSC pour ${deriveVscEstablishmentName(vscReport) || 'cet établissement'}`;
}

/** Detail badge color: `validated` is success; everything else uses the error token. */
export function getVscDetailStatusColor(status: VscReport['status']): 'success' | 'error' {
  return status === 'validated' ? 'success' : 'error';
}

/** Unlock-session HH:MM–HH:MM, or start only. */
export function formatVscSessionTimeRange(vscReport: VscReport): string | undefined {
  const start = vscReport.unlockSession?.startTime;
  if (!start) return undefined;
  const startLabel = start.slice(0, 5);
  const end = vscReport.unlockSession?.endTime;
  return end ? `${startLabel} – ${end.slice(0, 5)}` : startLabel;
}

/** Detail header subtitle: establishment · long report date. */
export function formatVscDetailHeaderSubtitle(vscReport: VscReport): string {
  return `${vscReport.establishment?.name ?? 'Établissement'} · ${formatYmd(vscReport.reportDate)}`;
}

/** Save toast — `submitted` vs draft save. */
export function getVscUpdateSuccessMessage(status?: string): string {
  return status === 'submitted' ? 'Rapport soumis' : 'Rapport enregistré';
}

/** Central error copy from {@link getErrorMessage}. */
export function getVscApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Status, volunteer, dates, and time range for the detail collapse. */
export function deriveVscDetailDisplay(vscReport: VscReport): DerivedVscDetailDisplay {
  const statusConfig =
    VSC_REPORT_STATUS_CONFIG[vscReport.status] ?? VSC_REPORT_STATUS_CONFIG.to_fill;
  return {
    statusConfig,
    StatusIcon: statusConfig.icon,
    volunteerName: deriveVscVolunteerName(vscReport),
    headerSubtitle: formatVscDetailHeaderSubtitle(vscReport),
    establishmentName: vscReport.establishment?.name ?? '—',
    reportDateLabel: formatYmd(vscReport.reportDate),
    sessionTimeRange: formatVscSessionTimeRange(vscReport),
    statusColor: getVscDetailStatusColor(vscReport.status),
  };
}

/**
 * Assigned VSC may edit after unlock; admin/manager/coordinator always may (until `validated`).
 * Submit needs a rating on `to_fill`; validate needs `submitted` plus elevated RBAC.
 */
export function getVscReportDetailCapabilities(
  input: VscReportDetailCapabilitiesInput,
): VscReportDetailCapabilities {
  const {
    vscReport,
    presentationMode,
    userId,
    isVolunteer,
    isVsc,
    isAdmin,
    isManager,
    isCoordinator,
    canValidate,
    draftRating,
  } = input;

  const isAssignedVsc =
    !!userId && (userId === vscReport.vscUserId || userId === vscReport.vscUser?.id);
  const canEditElevated = isAdmin || isCoordinator || isManager;
  const editable =
    presentationMode !== 'view' &&
    vscReport.status !== 'validated' &&
    ((isAssignedVsc && (isVolunteer || isVsc) && isVscReportVisibleNow(vscReport)) ||
      canEditElevated);

  return {
    editable,
    formDisabled: !editable,
    canSubmit: editable && draftRating != null && vscReport.status === 'to_fill',
    canValidateUi:
      presentationMode !== 'view' &&
      canValidate &&
      (isAdmin || isManager || isCoordinator) &&
      vscReport.status === 'submitted',
  };
}

/** True when the row `action-delete-row` permission allows delete. */
export function canDeleteVscReport(
  report: VscReport,
  pageActions?: Pick<VscReportPageActions, 'canDeleteRow'>,
): boolean {
  return getRowPermission(report, 'action-delete-row', pageActions?.canDeleteRow ?? true);
}

/** True when the user can validate and the report is `submitted`. */
export function canValidateVscReport(report: VscReport, canValidate: boolean): boolean {
  return canValidate && report.status === 'submitted';
}

/** True when the row can be selected (delete or validate). */
export function isVscReportSelectable(
  report: VscReport,
  canValidate: boolean,
  pageActions?: Pick<VscReportPageActions, 'canDeleteRow'>,
): boolean {
  return canDeleteVscReport(report, pageActions) || canValidateVscReport(report, canValidate);
}

/** Validate permission only when the caller is admin, manager, or coordinator. */
export function resolveElevatedValidatePermission(
  canValidate: boolean,
  roles: { isAdmin: boolean; isManager: boolean; isCoordinator: boolean },
): boolean {
  return canValidate && (roles.isAdmin || roles.isManager || roles.isCoordinator);
}

/** Selectable / selected / deletable / validatable slices for the bulk bar. */
export function deriveBulkSelectionState(
  data: VscReport[],
  selectedIds: string[],
  canValidateReport: boolean,
  pageActions?: VscReportPageActions,
): {
  selectableReports: VscReport[];
  selectableIds: string[];
  selectedReports: VscReport[];
  selectedDeletableReports: VscReport[];
  selectedValidatableReports: VscReport[];
} {
  const selectableReports = data.filter((report) =>
    isVscReportSelectable(report, canValidateVscReport(report, canValidateReport), pageActions),
  );
  const selectedReports = data.filter((report) => selectedIds.includes(report.id));
  return {
    selectableReports,
    selectableIds: selectableReports.map((report) => report.id),
    selectedReports,
    selectedDeletableReports: selectedReports.filter((report) =>
      canDeleteVscReport(report, pageActions),
    ),
    selectedValidatableReports: selectedReports.filter((report) =>
      canValidateVscReport(report, canValidateReport),
    ),
  };
}
